import React, { Fragment, useContext, useMemo, useState } from "react"
import { UseFormReturn, Controller, Control, FormState } from "react-hook-form"
import "react-dates/initialize"
import "react-dates/lib/css/_datepicker.css"

import { AdminContext } from "~/contexts/AdminContext"
import InputFieldWithLabel from "../../Inputs/InputFieldWithLabel"
import classNames from "../../../../utils/classNames"
import { CampProps } from "../../../../interfaces"
import CheckboxWithLabelForForm from "../../../ui/CheckboxWithLabelForForm"
import RichTextInput from "../../../ui/RichTextInput"
import { Listbox, Transition } from "@headlessui/react"
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/solid"
import { FormValues } from "~/components/Admin/Settings/pages/pricing/types"
import { dateFormats } from "~/constants/dateFormats"
import EditCampSkeleton from "~/components/Admin/Settings/skeletons/EditCampForm"
import { TextInputSkeleton } from "~/components/Admin/Settings/skeletons/TextInputSkeleton"
import CampLogoUpload from "~/components/Admin/Settings/components/CampLogoUpload"
import CampImagesUpload from "~/components/Admin/Settings/components/CampImagesUpload"
import CampMapUpload from "~/components/Admin/Settings/components/CampMapUpload"
import { EditCampFormProps } from "./interfaces/EditCampFormProps"
import { SeasonDatesFields } from "./components/SeasonDatesFields"
import { getIsFirstPartOfDateTypeMonth } from "./components/SeasonDatesFields/helpers/getFirstPartOfDateType"
import { TagsInput } from "react-tag-input-component"

const beforeAddValidate = (value: string) => /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(value)

const EditCampForm = ({ reactHookForm }: EditCampFormProps) => {
  const {
    control,
    watch,
    formState: { errors },
  } = reactHookForm
  const { isParkAdminUser, camp } = useContext(AdminContext)

  const date_format = watch("date_format")
  const primary_color = watch("primary_color")
  const secondary_color = watch("secondary_color")

  if (!camp) return <EditCampSkeleton />

  return (
    <div className="pb-20">
      <div className="my-6 space-y-1 font ">
        <div className="text-lgxl font-semibold text-gray-900">Camp Information</div>
        <div className="font-medium text-gray-500">Info shown in the booking flow for campers about your camp.</div>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <CampLogoUpload camp={camp} />
        <CampMapUpload camp={camp} />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TextInput field="name" label="Name" control={control} />
        <div className="flex gap-4">
          <ColorInput field="primary_color" label="Primary color" control={control} color={primary_color} />
          <ColorInput field="secondary_color" label="Secondary color" control={control} color={secondary_color} />
        </div>
        <TextInput field="email" label="Email" inputType="email" control={control} errors={errors} />
        <TextInput field="phone" label="Phone" control={control} />
        <div className="col-span-full grid gap-4 grid-cols-4">
          <TextInput field="address" label="Address" control={control} />
          <TextInput field="city" label="City" control={control} />
          <TextInput field="state" label="State" control={control} />
          <TextInput field="zip" label="Zip" control={control} />
        </div>
        <div className="col-span-full">
          <label className="block font-medium text-md text-gray-900">Bio</label>
          <Controller
            name="bio"
            control={control}
            render={({ field }) => (
              <RichTextInput value={field.value} setValue={field.onChange} style={{ marginTop: 3.5 }} hyperlink />
            )}
          />
        </div>
      </div>
      <hr className="my-6" />

      <div className="my-6 space-y-1 font">
        <div className="text-lgxl font-semibold text-gray-900">Notifications</div>
        <div className="font-medium text-gray-500">
          Configure who receives notifications about camp activity like reservations.
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="block font-medium text-md text-gray-900">Email</label>
          <div className="mt-1">
            <Controller
              name="notification_emails"
              control={control}
              render={({ field }) => (
                <TagsInput
                  value={field.value || []}
                  onChange={field.onChange}
                  name="notification_emails"
                  separators={[",", "Enter", " ", "Tab"]}
                  beforeAddValidate={beforeAddValidate}
                  classNames={{
                    tag: "",
                    input: "focus:outline-none focus:ring-0 focus:ring-offset-0",
                  }}
                  onBlur={(e: any) => {
                    const value = e.target.value.trim()
                    if (
                      value?.length &&
                      !field.value.includes(value) &&
                      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(value)
                    ) {
                      field.onChange([...field.value, value])
                      e.target.value = ""
                    }
                  }}
                />
              )}
            />
          </div>
        </div>
      </div>

      <hr className="my-6" />

      <div className="text-lgxl font-bold">Season Dates</div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <SeasonDatesFields
          label="Season start"
          isMonthFirst={getIsFirstPartOfDateTypeMonth(camp.date_format)}
          field_mm={"season_start_mm"}
          field_dd={"season_start_dd"}
          reactHookForm={reactHookForm}
        />
        <SeasonDatesFields
          label="Season end"
          isMonthFirst={getIsFirstPartOfDateTypeMonth(camp.date_format)}
          field_mm={"season_end_mm"}
          field_dd={"season_end_dd"}
          reactHookForm={reactHookForm}
        />
        <SeasonDatesFields
          label="Booking start"
          isMonthFirst={getIsFirstPartOfDateTypeMonth(camp.date_format)}
          field_mm={"booking_start_mm"}
          field_dd={"booking_start_dd"}
          reactHookForm={reactHookForm}
        />
      </div>

      <hr className="my-6" />

      <div className="text-lgxl font-bold">Amenities Info</div>
      <div className="flex flex-col gap-4">
        <div className="grid xs:grid-cols-1 md:grid-cols-3 gap-4">
          {["gate_code", "bathroom_code", "laundry_code"].map((field) => (
            <TextInput key={field} field={field} label={field.replace(/_/g, " ")} control={control} />
          ))}
        </div>
        <div className="grid xs:grid-cols-1 md:grid-cols-2 gap-4">
          {["wifi_network", "wifi_password"].map((field) => (
            <TextInput key={field} field={field} label={field.replace(/_/g, " ")} control={control} />
          ))}
        </div>
      </div>

      <hr className="my-6" />
      <CampImagesUpload camp={camp} />

      {isParkAdminUser && (
        <div>
          <hr className="my-6" />

          <div className="text-lgxl font-bold">Park Admin fields</div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {["allow_same_day_reservations", "allow_rv_on_tent_site"].map((field) => (
              <Controller
                key={field}
                name={field}
                control={control}
                render={({ field }) => (
                  <CheckboxWithLabelForForm
                    name={field.name}
                    label={field.name.replace(/_/g, " ")}
                    checked={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextInput
              field="same_day_reservations_cutoff_time"
              label="Same day reservation cutoff time"
              control={control}
              disabled={!watch("allow_same_day_reservations")}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {["stripe_account_id", "slug", "shortened_slug", "timezone_offset", "timezone", "bookable_for_n_days"].map(
              (field) => (
                <TextInput key={field} field={field} label={field.replace(/_/g, " ")} control={control} />
              ),
            )}
          </div>
        </div>
      )}
      <hr className="my-6" />

      <div className="text-lgxl font-bold">Date Format</div>
      <Controller
        name="date_format"
        control={control}
        render={({ field }) => (
          <Listbox value={field.value} onChange={field.onChange}>
            <div className="relative mt-1">
              <Listbox.Button className="relative w-full cursor-default rounded-md bg-white border border-gray-300 shadow-sm focus:border-green-600 focus:ring-green-600 py-2 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:leading-6">
                <span className="block truncate">{field.value}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </Listbox.Button>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                  {dateFormats.map((format, i) => (
                    <Listbox.Option
                      key={i}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                          active ? "bg-amber-100 text-amber-900" : "text-gray-900"
                        }`
                      }
                      value={format}
                    >
                      <span className={`block truncate ${format === field.value ? "font-medium" : "font-normal"}`}>
                        {format}
                      </span>
                      {format === field.value && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
        )}
      />
    </div>
  )
}

export const TextInput = ({
  field,
  label,
  control,
  inputType = "text",
  className,
  inputClassName,
  disabled,
  errors,
  error,
}: {
  field: keyof CampProps
  label: string
  control: Control<CampProps>
  inputType?: string
  className?: string
  disabled?: boolean
  inputClassName?: string
  error?: string
  errors?: FormState<FormValues>
}) => {
  const { camp } = useContext(AdminContext)

  if (!camp) {
    return <TextInputSkeleton className={className} inputClassName={inputClassName} />
  }

  const fieldError = Object.values(errors?.[field] || {})
    .filter((item) => typeof item === "string")
    ?.join("")

  return (
    <div className={classNames("mb-2", className)}>
      <Controller
        name={field as string}
        control={control}
        defaultValue={camp[field]} // Use default value from camp context
        render={({ field: { onChange, onBlur, value } }) => (
          <InputFieldWithLabel
            label={label}
            name={field}
            type={inputType}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            inputClassName={inputClassName}
            error={error || fieldError}
          />
        )}
      />
    </div>
  )
}

// ColorInput Component with Controller
export const ColorInput = ({
  field,
  label,
  color,
  control,
  className,
}: {
  field: string
  label: string
  control: any
  color: string
  className?: string
}) => {
  return (
    <div className="w-full">
      <label className="block text-md font-medium leading-6 text-gray-900">{label}</label>
      <div className="relative mt-1 rounded-md shadow-sm bg-white w-full border-gray-300 border">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <div className="rounded-full w-5 h-5" style={{ backgroundColor: color }}></div>
        </div>
        <div className="absolute text-gray-700 h-full flex items-center inset-y-0 left-11">{color}</div>
        <Controller
          name={field as string}
          control={control}
          render={({ field: { onChange, value } }) => (
            <input
              type="color"
              value={value || color}
              onChange={onChange}
              className="block opacity-0 cursor-text w-full rounded-md bg-white border-gray-300 shadow-sm focus:border-green-600 focus:ring-green-600 py-5 pl-10 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:leading-6"
            />
          )}
        />
      </div>
    </div>
  )
}

export default EditCampForm
